import * as React from "react";
import { cn } from "@repo/utils/cn";
import { ComponentProps } from "react";
import Image from "next/image";
import { parseAiAlt } from "@repo/ai-disclaimer";
import { AiDisclaimerForAlt } from "./AiDisclaimer";
import { Button, ButtonProps } from "./Button";
import { Link } from "./Link";
import { LinkProps } from "./types";
import { getImageSizes } from "./utils";

export interface CardParkingFeature {
  icon?: {
    src: string;
    alt?: string;
  } | null;
  text?: string;
}

export interface CardParkingProps
  extends Omit<ComponentProps<"div">, "children"> {
  image?: {
    src: string;
    alt?: string;
    width?: number;
    height?: number;
  } | null;
  tariff?: string;
  title?: string;
  description?: React.ReactNode;
  features?: CardParkingFeature[];
  /** Output of the Storyblok button resolver (link, anchor or Navattic demo). */
  button?: ButtonProps | null;
  link?: LinkProps | null;
  aspectRatio?: "square" | "tall";
  sizes?: string;
  onClick?: () => void;
}

export const CardParking: React.FC<CardParkingProps> = ({
  image,
  tariff,
  title,
  description,
  features,
  button,
  link,
  aspectRatio = "tall",
  sizes,
  onClick,
  className,
  ...props
}) => {
  const imageSizes = sizes || getImageSizes(image?.src);

  // Parse tariff to separate prefix and price for different font weights
  const parsedTariff = React.useMemo(() => {
    if (!tariff) return null;
    const trimmed = tariff.trim();
    const lowerTrimmed = trimmed.toLowerCase();

    // Check if it already starts with "from"
    if (lowerTrimmed.startsWith("from")) {
      // Extract the price part (everything after "from" and whitespace)
      const afterFrom = trimmed.slice(4).trimStart();
      if (afterFrom) {
        return { prefix: "from ", price: afterFrom };
      }
      return { prefix: "", price: trimmed };
    }

    // Add "from " prefix
    return { prefix: "from ", price: trimmed };
  }, [tariff]);

  const cardClasses = cn(
    "uipm:group uipm:relative uipm:overflow-hidden uipm:rounded-[var(--radius-sm)]",
    "uipm:flex uipm:flex-col uipm:justify-end",
    "uipm:min-h-[300px]",
    "uipm:cursor-pointer",
    aspectRatio === "square" ? "uipm:aspect-[1/1]" : "uipm:aspect-[291/416]",
    link && "uipm:no-underline",
    className,
  );

  const cardContent = (
    <>
      {image && (
        <>
          <div className="uipm:absolute uipm:inset-0 uipm:transition-transform uipm:duration-300 uipm:ease-out uipm:group-hover:scale-110">
            <Image
              src={image.src}
              alt={parseAiAlt(image.alt).cleanAlt}
              fill
              sizes={imageSizes}
              className="uipm:object-cover uipm:rounded-[var(--radius-sm)]"
            />
          </div>
          {/* Outside the scaling wrapper: the label must not zoom with the image. */}
          <AiDisclaimerForAlt alt={image.alt} src={image.src} />
        </>
      )}

      {/* Gradient Overlay */}
      <div
        className="uipm:absolute uipm:inset-0 uipm:z-[5] uipm:opacity-80 uipm:transition-opacity uipm:duration-300 uipm:ease-out uipm:group-hover:opacity-40"
        style={{
          background:
            "linear-gradient(180deg, rgba(36, 36, 36, 0.00) 0%, rgba(36, 36, 36, 1) 59.62%)",
        }}
      />

      {/* Content Section */}
      <div
        className={cn(
          "uipm:relative",
          "uipm:flex uipm:flex-col",
          "uipm:gap-[var(--padding-padding-sm-plus)]",
          "uipm:pt-[var(--container-card-medium-padding)]",
          "uipm:px-[var(--padding-padding-sm-plus)]",
          "uipm:pb-[var(--padding-padding-default)]",
          "uipm:z-10",
        )}
      >
        {parsedTariff && (
          <div
            className={cn(
              "uipm:text-[var(--typography-colors-link)]",
              "uipm:text-(length:--typography-sizes-body-small)",
              "uipm:font-medium",
              "uipm:leading-[var(--typography-line-heights-body-xs)]",
              "uipm:bg-[var(--surface-surface-primary)]",
              "uipm:rounded-[var(--radius-radius-full)]",
              "uipm:px-[var(--margin-margin-sm-plus)]",
              "uipm:py-[6px]",
              "uipm:inline-block uipm:w-fit",
            )}
          >
            {parsedTariff.prefix}
            <span className="uipm:font-bold">{parsedTariff.price}</span>
          </div>
        )}

        <div className="uipm:flex uipm:flex-col uipm:gap-1">
          {title && (
            <h3
              className={cn(
                "uipm:font-[var(--font-family-sans)]",
                "uipm:text-[var(--typography-colors-text-inverted)]",
                "uipm:text-(length:--typography-sizes-body-large)",
                "uipm:font-bold",
                "uipm:leading-[var(--typography-line-heights-body-large)]",
                "uipm:text-balance",
              )}
            >
              {title}
            </h3>
          )}

          {description && (
            <div
              className={cn(
                "uipm:font-[var(--font-family-sans)]",
                "uipm:text-[var(--typography-colors-text-inverted)]",
                "uipm:text-(length:--typography-sizes-body-small)",
                "uipm:font-normal",
                "uipm:leading-[var(--typography-line-heights-body-xs)]",
              )}
            >
              {description}
            </div>
          )}

          {features && features.length > 0 && (
            <div className="uipm:flex uipm:gap-[var(--container-gutter)] uipm:flex-wrap">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="uipm:flex uipm:items-center uipm:gap-1"
                >
                  {feature.icon && (
                    <Image
                      src={feature.icon.src}
                      alt={feature.icon.alt || ""}
                      width={16}
                      height={16}
                      className="uipm:w-4 uipm:h-4"
                    />
                  )}
                  {feature.text && (
                    <span
                      className={cn(
                        "uipm:font-[var(--font-family-sans)]",
                        "uipm:text-[var(--typography-colors-text-inverted)]",
                        "uipm:text-(length:--typography-sizes-body-small)",
                        "uipm:font-normal",
                        "uipm:leading-[var(--typography-line-heights-body-xs)]",
                      )}
                    >
                      {feature.text}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {button && (
          // A linked card covers its whole surface with a z-20 <Link>; a Navattic
          // CTA has no href to share with it, so it must sit above the overlay.
          <div
            className={cn(
              link && button.navatticUrl && "uipm:relative uipm:z-30",
            )}
          >
            <Button
              href={button.href}
              target={button.target}
              onClick={button.onClick}
              navatticUrl={button.navatticUrl}
              navatticTitle={button.navatticTitle}
              navatticMobileUrl={button.navatticMobileUrl}
              noFollow={button.noFollow}
              disabled={button.disabled}
              variant={button.variant || "inverted-border"}
              size={button.size || "small"}
              className="uipm:w-full"
            >
              {button.text}
            </Button>
          </div>
        )}
      </div>
    </>
  );

  if (link) {
    return (
      <div className={cardClasses} {...props}>
        <Link
          href={link.href}
          target={link.target}
          noFollow={link.noFollow}
          onClick={onClick}
          className="uipm:absolute uipm:inset-0 uipm:z-20"
        >
          <span className="uipm:sr-only">{title}</span>
        </Link>
        {cardContent}
      </div>
    );
  }

  return (
    <div className={cardClasses} onClick={onClick} {...props}>
      {cardContent}
    </div>
  );
};
